import type { Icon } from '@phosphor-icons/react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/cn'

/**
 * The "card + number" tile that opens every queue: a bare Phosphor glyph and
 * label on top, a large tabular count, and an optional secondary line (rupee
 * value, or a hint about what the count covers) beneath it.
 */
export function MetricCard({
  icon: Glyph,
  label,
  value,
  amount,
  loading = false,
  title,
  className,
}: {
  icon: Icon
  label: string
  value: string
  /** Secondary line beneath the count. Omit for count-only cards. */
  amount?: string
  loading?: boolean
  /** Native tooltip for the whole card. */
  title?: string
  className?: string
}) {
  return (
    <Card
      data-slot="metric-card"
      title={title}
      className={cn('flex flex-col gap-4 p-4', className)}
    >
      <div className="flex items-center gap-2 whitespace-nowrap text-[12.5px] font-medium text-[color:var(--muted-foreground)]">
        <Glyph
          aria-hidden
          className="size-4 shrink-0 text-[color:var(--foreground)]"
          weight="duotone"
        />
        {label}
      </div>
      <div className="flex flex-col gap-0.5">
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <span className="text-[24px] font-bold leading-7 tracking-[-0.02em] tabular-nums text-[color:var(--foreground)]">
            {value}
          </span>
        )}
        {amount !== undefined ? (
          loading ? (
            <Skeleton className="mt-1 h-3.5 w-16" />
          ) : (
            <span className="whitespace-nowrap text-[12.5px] tabular-nums text-[color:var(--muted-foreground)]">
              {amount}
            </span>
          )
        ) : null}
      </div>
    </Card>
  )
}
